import Big from 'big.js'
import { Measure } from '../types'


enum ForceMeasurementSystem {
  siUnits = 'SI Units',
  metricDerived = 'Metric Derived Units',
  usImperialUnits = 'US Customary and Imperial Units',
  cgsSystem = 'CGS System Unit'
}

const forceUnits = {
  newton: {
    displayName: 'Newton',
    codeName: 'newton',
    url: 'newton',
    symbol: 'N',
    toBaseUnit: (value: Big): Big => value,
    fromBaseUnit: (value: Big): Big => value,
    system: ForceMeasurementSystem.siUnits
  },
  kilonewton: {
    displayName: 'Kilonewton',
    codeName: 'kilonewton',
    url: 'kilonewton',
    symbol: 'kN',
    toBaseUnit: (value: Big): Big => value.times(1000),
    fromBaseUnit: (value: Big): Big => value.div(1000),
    system: ForceMeasurementSystem.siUnits
  },
  meganewton: {
    displayName: 'Meganewton',
    codeName: 'meganewton',
    url: 'meganewton',
    symbol: 'MN',
    toBaseUnit: (value: Big): Big => value.times(1000000),
    fromBaseUnit: (value: Big): Big => value.div(1000000),
    system: ForceMeasurementSystem.siUnits
  },
  giganewton: {
    displayName: 'Giganewton',
    codeName: 'giganewton',
    url: 'giganewton',
    symbol: 'GN',
    toBaseUnit: (value: Big): Big => value.times(1000000000),
    fromBaseUnit: (value: Big): Big => value.div(1000000000),
    system: ForceMeasurementSystem.siUnits
  },
  teranewton: {
    displayName: 'Teranewton',
    codeName: 'teranewton',
    url: 'teranewton',
    symbol: 'TN',
    toBaseUnit: (value: Big): Big => value.times(1000000000000),
    fromBaseUnit: (value: Big): Big => value.div(1000000000000),
    system: ForceMeasurementSystem.siUnits
  },

  gram_force: {
    displayName: 'Gram-force',
    codeName: 'gram_force',
    url: 'gram-force',
    symbol: 'gf',
    toBaseUnit: (value: Big): Big => value.times(0.00980664999999931),
    fromBaseUnit: (value: Big): Big => value.div(0.00980664999999931),
    system: ForceMeasurementSystem.metricDerived
  },
  kilogram_force: {
    displayName: 'Kilogram-force',
    codeName: 'kilogram_force',
    url: 'kilogram-force',
    symbol: 'kgf',
    toBaseUnit: (value: Big): Big => value.times(9.80664999999931),
    fromBaseUnit: (value: Big): Big => value.div(9.80664999999931),
    system: ForceMeasurementSystem.metricDerived
  },
  ton_force_metric: {
    displayName: 'ton-force (Metric)',
    codeName: 'ton_force_metric',
    url: 'ton-force-metric',
    symbol: 'tf',
    toBaseUnit: (value: Big): Big => value.times(9806.64999999931),
    fromBaseUnit: (value: Big): Big => value.div(9806.64999999931),
    system: ForceMeasurementSystem.metricDerived
  },

  ounce_force: {
    displayName: 'Ounce-force',
    codeName: 'ounce_force',
    url: 'ounce-force',
    symbol: 'ozf',
    toBaseUnit: (value: Big): Big => value.times(0.278013850953423),
    fromBaseUnit: (value: Big): Big => value.div(0.278013850953423),
    system: ForceMeasurementSystem.usImperialUnits
  },
  pound_force: {
    displayName: 'Pound-force',
    codeName: 'pound_force',
    url: 'pound-force',
    symbol: 'lbf',
    toBaseUnit: (value: Big): Big => value.times(4.44822161525477),
    fromBaseUnit: (value: Big): Big => value.div(4.44822161525477),
    system: ForceMeasurementSystem.usImperialUnits
  },
  kip_force: {
    displayName: 'Kip-force',
    codeName: 'kip_force',
    url: 'kip-force',
    symbol: 'kipf',
    toBaseUnit: (value: Big): Big => value.times(4448.22161525477),
    fromBaseUnit: (value: Big): Big => value.div(4448.22161525477),
    system: ForceMeasurementSystem.usImperialUnits
  },
  ton_force_uk: {
    displayName: 'ton-force (Short/UK)',
    codeName: 'ton_force_uk',
    url: 'ton-force-uk',
    symbol: 'tonf',
    toBaseUnit: (value: Big): Big => value.times(9964.01641817069),
    fromBaseUnit: (value: Big): Big => value.div(9964.01641817069),
    system: ForceMeasurementSystem.usImperialUnits
  },
  ton_force_us: {
    displayName: 'ton-force (Long/US)',
    codeName: 'ton_force_us',
    url: 'ton-force-us',
    symbol: 'tonf',
    toBaseUnit: (value: Big): Big => value.times(8896.443230521),
    fromBaseUnit: (value: Big): Big => value.div(8896.443230521),
    system: ForceMeasurementSystem.usImperialUnits
  },

  dynes: {
    displayName: 'Dynes',
    codeName: 'dynes',
    url: 'dynes',
    symbol: 'dyn',
    toBaseUnit: (value: Big): Big => value.times(0.00001),
    fromBaseUnit: (value: Big): Big => value.div(0.00001),
    system: ForceMeasurementSystem.cgsSystem
  }
}


export const force: Measure = {
  displayName: 'Force',
  codeName: 'force',
  url: 'force',
  units: forceUnits,
  baseUnit: forceUnits.newton,
  defaultFromUnit: forceUnits.newton,
  defaultToUnit: forceUnits.pound_force
}
